<template>
  <form id="form_1" action="" method="post" role="form">
    <div class="error-container"></div>

    <div class="row">
      <div class="col-md-12">
        <p>
          <strong class="font-purple font-120P">Expression of Interest</strong>
        </p>
      </div>
      <div class="col-md-12">
        <ul>
          <li>
            Enter the details below to begin your Expression of Interest (EOI). 
            You can save your progress and return later via the Opportunities tab in your Customer Dashboard.
          </li>
          <li>
            After submitting these details, you will receive an email with a verification code. Once verified, 
            you’ll be directed to the EOI form to sign and complete your submission.
          </li>
        </ul>
      </div>
    </div>

    <div class="gap-40"></div>

    <div class="row">
      <div class="col-md-10">
        <p>
          <strong class="font-120P"
            >Proposal Details</strong
          >
        </p>
      </div>
      <div class="col-md-2 text-right">
      </div>
      <div class="col-md-12">
        <ul>
          <li>
            <strong>Retention:</strong> 20% of the purchase price to be held in trust by the Vendor’s solicitor/conveyancer for a period of 90 days post-settlement
          </li>
          <li>
            <strong>Due Diligence:</strong> Seven (7) days allowed from finance approval or contract execution to conduct due diligence.
          </li>
        </ul>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="purchase_price">Proposed Purchase Price ($) *</label>
          <input
            class="form-control"
            id="purchase_price"
            name="purchase_price"
            :disabled="disableField"
            placeholder="Proposed Purchase Price ($)"
            :class="{ 'has-error': errors?.hasOwnProperty('purchase_price') }"
            type="number"
            v-model="values.purchase_price"
            required
          />
          <span
            v-if="errors?.hasOwnProperty('purchase_price')"
            class="help-block text-danger"
            >{{ errors.purchase_price[0] }}</span
          >
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="settlement_date">Proposed Settlement Date</label>
          <div class="input-group date" id="settlement_date_picker">
            <DatePicker
              v-model="values.settlement_date"
              @update="e => {values.settlement_date = e.target.value;}"
              name="settlement_date"
              id="settlement_date"
              :disabled="disableField"
              :input-class="
                `form-control ${
                  errors?.hasOwnProperty('settlement_date') ? 'has-error' : ''
                }`
              "
              placeholder="Proposed Settlement Date"
            />
          </div>
          <span
            v-if="errors?.hasOwnProperty('settlement_date')"
            class="help-block text-danger"
            >{{ errors.settlement_date[0] }}</span
          >
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="settlement_date">Proposed Deposit</label>
          <input
            class="form-control"
            id="deposit"
            name="proposed_deposit"
            :disabled="disableField"
            placeholder="Proposed Deposit ($)"
            :class="{ 'has-error': errors?.hasOwnProperty('deposit') }"
            type="number"
            v-model="values.proposed_deposit"
            required
          />
          <span
            v-if="errors?.hasOwnProperty('proposed_deposit')"
            class="help-block text-danger"
            >{{ errors.proposed_deposit[0] }}</span
          >
        </div>
      </div>
    </div>
    <div class="gap-40"></div>

    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <label for="bank">Lender</label>
          <input
            class="form-control"
            id="bank"
            name="bank"
            :disabled="disableField"
            :class="{ 'has-error': errors?.hasOwnProperty('contract.bank') }"
            placeholder="Lender"
            type="text"
            v-model="values.contract.bank"
          />
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="finance_amount">Loan Amount ($)</label>
          <input
            class="form-control"
            id="finance_amount"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('contract.finance_amount')
            }"
            name="finance_amount"
            placeholder="Loan amount ($)"
            type="number"
            v-model="values.contract.finance_amount"
          />
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="interest_rate">Interest Rate (%)</label>
          <input
            class="form-control"
            id="interest_rate"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('contract.interest_rate')
            }"
            name="interest_rate"
            placeholder="Interest rate (%)"
            type="number"
            v-model="values.contract.interest_rate"
          />
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="settlement_date">Approval Deadline</label>
          <div class="input-group date" id="approval_date_picker">
            <DatePicker
              v-model="values.contract.approval_date"
              @update="e => {values.contract.approval_date = e.target.value;}"
              name="approval_date"
              id="approval_date"
              :disabled="disableField"
              :input-class="
                `form-control ${
                  errors?.hasOwnProperty('contract.approval_date')
                    ? 'has-error'
                    : ''
                }`
              "
              placeholder="Approval deadline"
            />
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <label for="other_details">Other Special Conditions:</label>
        <div class="form-group">
          <textarea
            class="form-control form-control-message"
            name="other_details"
            id="other_details"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('contract.other_details')
            }"
            placeholder="Other Details"
            v-model="values.contract.other_details"
            rows="5"
          ></textarea>
        </div>
      </div>
    </div>

    <div class="gap-40"></div>

    <div class="row">
      <div class="col-md-10">
        <p>
          <strong class="font-120P">Purchaser Details</strong>
        </p>
      </div>
      <div class="col-md-2 text-right">
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="trading_name">Trading Name</label>
          <input
            class="form-control"
            id="trading_name"
            :disabled="disableField"
            name="trading_name"
            placeholder="Trading name"
            :class="{
              'has-error': errors?.hasOwnProperty('business.trading_name')
            }"
            type="text"
            v-model="values.business.trading_name"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="business_name">Business Name</label>
          <input
            class="form-control"
            id="business_name"
            :disabled="disableField"
            name="business_name"
            :class="{ 'has-error': errors?.hasOwnProperty('business.name') }"
            placeholder="Business name"
            type="text"
            v-model="values.business.name"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="business_abn">ABN </label>
          <input
            class="form-control"
            id="business_abn"
            :disabled="disableField"
            name="business_abn"
            :class="{ 'has-error': errors?.hasOwnProperty('business.abn') }"
            placeholder="ABN"
            type="text"
            v-model="values.business.abn"
          />
        </div>
      </div>
      <div class="col-md-12">
        <div class="form-group">
          <label for="street_address">Business Address *</label>
          <input
            class="form-control"
            id="street_address"
            :disabled="disableField"
            name="street_address"
            :class="{
              'has-error': errors?.hasOwnProperty('business.street_address')
            }"
            placeholder="Business address"
            type="text"
            v-model="values.business.street_address"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="director_name">Director/Sole Trader Name(s)</label>
          <input
            class="form-control"
            id="director_name"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('business.director_name')
            }"
            name="director_name"
            placeholder="Director's Name(s)"
            type="text"
            v-model="values.business.director_name"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label for="first_name">Contact Name *</label>
          <input
            class="form-control"
            id="first_name"
            :disabled="disableField"
            name="first_name"
            :class="{
              'has-error': errors?.hasOwnProperty('representative.first_name')
            }"
            placeholder="Contact name"
            type="text"
            v-model="values.representative.first_name"
            required
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="email">Email *</label>
          <input
            class="form-control"
            id="email"
            name="email"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('representative.email')
            }"
            placeholder="Email"
            type="email"
            v-model="values.representative.email"
            required
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="contact_phone">Phone *</label>
          <input
            class="form-control"
            id="contact_phone"
            name="contact_phone"
            :disabled="disableField"
            :class="{
              'has-error': errors?.hasOwnProperty('representative.contact_phone')
            }"
            placeholder="Phone"
            type="text"
            v-model="values.representative.contact_phone"
            required
          />
        </div>
      </div>
    </div>
    <div class="gap-40"></div>

    <div class="row">
      <div class="col-md-10">
        <p>
          <strong class="font-120P">Legal Representative Details</strong>
        </p>
      </div>
      <div class="col-md-2 text-right">
      </div>
      <div class="col-md-12">
        <div class="form-group">
          <label for="conveyance_solicitor_first_name">Firm Name *</label>
          <input
            class="form-control"
            id="conveyance_solicitor_company_name"
            :disabled="disableField"
            name="conveyance_solicitor_company_name"
            :class="{
              'has-error': errors?.hasOwnProperty('solicitor.company_name')
            }"
            placeholder="Firm name"
            type="text"
            v-model="values.solicitor.company_name"
            required
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="conveyance_solicitor_first_name"
            >Contact Name *</label
          >
          <input
            class="form-control"
            id="conveyance_solicitor_first_name"
            name="conveyance_solicitor_first_name"
            :disabled="disableField"
            placeholder="Contact name"
            :class="{
              'has-error': errors?.hasOwnProperty('solicitor.first_name')
            }"
            type="text"
            v-model="values.solicitor.first_name"
            required
          />
        </div>
      </div>
      
      <div class="col-md-6">
        <div class="form-group">
          <label for="conveyance_solicitor_contact_phone"
            >Phone *</label
          >
          <input
            class="form-control"
            id="conveyance_solicitor_contact_phone"
            name="conveyance_solicitor_contact_phone"
            :disabled="disableField"
            placeholder="Phone"
            type="text"
            :class="{
              'has-error': errors?.hasOwnProperty('solicitor.contact_phone')
            }"
            v-model="values.solicitor.contact_phone"
            required
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="conveyance_solicitor_email">Email *</label>
          <input
            class="form-control"
            id="conveyance_solicitor_email"
            :disabled="disableField"
            name="conveyance_solicitor_email"
            placeholder="Email"
            :class="{ 'has-error': errors?.hasOwnProperty('solicitor.email') }"
            type="email"
            v-model="values.solicitor.email"
            required
          />
        </div>
      </div>
    </div>
    <div class="gap-40"></div>

    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <SubmitButton
            v-if="expression.status !== 'Submitted' && this.customer !== null"
            :isProgress="isLoading"
            :caption="
              isLoading ? 'Submitting' : 'Submit Details'
            "
            @submit="handleSubmit('Submitted')"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import DatePicker from "@/components/Form/DatePicker";
import Select from "@/components/Form/Select";
import SaveBtn from "@/components/SaveBtn";
import SubmitButton from "@/components/SubmitButton";

import map from "lodash-es/map";
import set from "lodash-es/set";
import get from "lodash-es/get";
import moment from "moment";
export default {
  computed: {
    getGeoLocations() {
      return this.$http;
    },
    disableField() {
      return this.expression && this.expression.status === "Submitted";
    }
  },
  components: {
    DatePicker,
    Select,
    SaveBtn,
    SubmitButton
  },
  props: {
    data: Object,
    expression: Object,
    customer: Object
  },
  data() {
    return {
      values: {
        contract: {},
        representative: {
          ...this.customer,
          contact_phone: this.customer ? this.customer.phone_number : ""
        },
        purchaser: {
          ...this.customer,
          contact_phone: this.customer ? this.customer.phone_number : ""
        },
        solicitor: {},
        guarantor: {},
        business: {
          ...this.customer?.business
        },
        interest_id: this.data ? this.data.interest_id : "",
        same_as_contact: this.expression
          ? !!this.expression.same_as_contact
          : true
      },
      errors: {},
      countrySearch: `${process.env.VUE_APP_GEO_LOCATION_API_URL}/countries/search`,
      stateSearch: `${process.env.VUE_APP_GEO_LOCATION_API_URL}/states/search`,
      isLoading: false
    };
  },
  methods: {
    handleDropdownChange(value, param) {
      set(this.values, param, value);
    },
    async handleSubmit(status) {
      this.isLoading = true;
      var data = null;
      try {
        data = await this.$http.post(
          `${process.env.VUE_APP_INTEREST_API_URL}/admin/expressions`,
          {
            ...this.values,
            business: {
              ...this.values.business
            },
            settlement_date: this.values.settlement_date
              ? moment(this.values.settlement_date).format("YYYY-MM-DD")
              : null,
            contract: {
              ...this.values.contract,
              approval_date:
                this.values.contract && this.values.contract.approval_date
                  ? moment(this.values.contract.approval_date).format(
                      "YYYY-MM-DD"
                    )
                  : null,
              finance_amount:
                this.values.contract && this.values.contract.finance_amount
                  ? this.values.contract.finance_amount
                  : null
            },
            status: status ? status : null,
            purchase_price: this.values.purchase_price
              ? this.values.purchase_price
              : null
          }
        );


        // If expression submitted successfully
        if (data !== null && data !== undefined) {
          if (data.status === "Submitted" || data.status === "Pending") {
            this.$emit("submitted");
          } else if (data.status === "Failed") {
            this.$emit("failed");
          } else if (data.status === "Draft") {
            this.$emit("drafted");
          }

          await this.$router.push({
            name: "email_verification",
            params: {interestID: data.interest.uuid, userUUID: data.interest.customer.user.identifier}
          });
        }

        /*
        if (this.expression) {
          await this.$router.push({
            name: "email_verification",
            params: {interestID: this.expression.interest.uuid, userUUID: this.expression.interest.customer.user.identifier}
          });
        }
        */

        this.isLoading = false;
      } catch (e) {
        this.errors = get(e, "data.errors");
        console.log(get(e, "data.errors"));
        this.isLoading = false;
      }
    },
    handleSameContact() {
      if (this.values.same_as_contact) {
        this.values.purchaser = {
          ...this.values.representative
        };
      } else {
        this.values.purchaser.first_name = null;
        this.values.purchaser.email = null;
        this.values.purchaser.contact_phone = null;
      }
    },
    async loadData() {
      map(this.expression, (value, index) => {
        const key = get(index.split("_"), "0");
        const valueKey = index.substring(index.indexOf("_") + 1);
        if (this.values.hasOwnProperty(key)) {
          this.values = {
            ...this.values,
            [key]: {
              ...this.values[key],
              [valueKey]:
                valueKey === "approval_date"
                  ? moment().format("YYYY-MM-DD")
                  : value
            }
          };
        }
      });
      if (this.expression) {
        this.values = {
          ...this.values,
          purchase_price: this.expression.purchase_price
            ? this.expression.purchase_price
            : null,
          proposed_deposit: this.expression.proposed_deposit
            ? this.expression.proposed_deposit
            : null,
          business: {
            ...this.values.business,
            ...this.expression.business,
            trading_name: this.expression.trading_name
              ? this.expression.trading_name
              : this.values.business.name,
            director_name: this.expression.director_name
              ? this.expression.director_name
              : null
          }
        };
        this.values.representative.first_name = this.values.representative.contact.first_name;
        this.values.representative.email = this.values.representative.contact.email;
        this.values.representative.contact_phone = this.values.representative.contact.phone_number;
        this.values.settlement_date = this.expression.settlement_date
          ? this.expression.settlement_date
          : moment().format("YYYY-MM-DD");
      }
    }
  },
  watch: {
    // eslint-disable-next-line vue/no-arrow-functions-in-watch
    expression: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue) {
          this.loadData();
        }
      }
    }
  }
};
</script>
